import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { useEffect, useRef, useContext } from "react";
import BryntumContext from "@/react/context/bryntumContext";
import type { EntityRecord } from "@/types/entity";
import { useEntityManagerContext } from "./context/EntityManagerContext";

interface BryntumWidgetInstance {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadRecord: (record: EntityRecord, context: any, options: { isFirstShow: boolean }) => void;
  tab: {
    element: HTMLElement | null;
  };
  swivuUpdateCount?: () => void;
  hidden: boolean;
  onTabChangeHandler?: (options: { activeItem: BryntumWidgetInstance }) => void;
  onSwivuEditorHide?: () => void;
}

interface BryntumWidgetRef {
  instance: BryntumWidgetInstance;
}

interface BryntumWidgetConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type: any;
}

interface EntityBryntumTabProps {
  widget: BryntumWidgetConfig;
  hidden: boolean;
  onVisible?: (params: {
    widget: BryntumWidgetInstance;
    record: EntityRecord;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
    isFirstShow: boolean;
  }) => void;
  onDependencyChange?: (params: {
    widget: BryntumWidgetInstance;
    isFirstShow: boolean;
    record: EntityRecord;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }) => void;
}

export function EntityBryntumTab({
  widget,
  hidden,
  onVisible,
  onDependencyChange
}: EntityBryntumTabProps) {
  const { record } = useEntityManagerContext();
  const ref = useRef<BryntumWidgetRef>(null);
  const context = useContext(BryntumContext);
  const isFirstShow = useRef(true);

  useEffect(() => {
    if (ref.current) {
      const widgetInstance = ref.current.instance;
      widgetInstance.loadRecord(record, context, { isFirstShow: isFirstShow.current });
    }
  }, [ref, record, context]);

  /* Handle init state */
  useEffect(() => {
    if (!ref.current) return;

    const widgetInstance = ref.current.instance;

    /* We have to set the hidden property of bryntum tab to toggle it without causing layout/ui issues on bryntum UI */
    widgetInstance.hidden = hidden;

    if (!hidden) {
      /* Call the onVisible callback */
      onVisible?.({ widget: widgetInstance, record, context, isFirstShow: isFirstShow.current });

      /* Internally some tabs rely on the onTabChangeHandler to handle data loads */
      widgetInstance.onTabChangeHandler?.({ activeItem: widgetInstance });

      /* Not is first show anymore */
      if (isFirstShow.current) isFirstShow.current = false;
    }
  }, [hidden, record, context, onVisible]);

  /* Callbacks when any dependency changes in the useEffect, Works for contact tab right now */
  useEffect(() => {
    if (ref.current) {
      const widgetInstance = ref.current.instance;
      onDependencyChange?.({ widget: widgetInstance, isFirstShow: isFirstShow.current, record, context });
    }
  }, [context.editorHandler, record, context, onDependencyChange]);

  /* Cleanup only on component unmount */
  useEffect(() => {
    return () => {
      const widgetInstance = ref.current?.instance;
      widgetInstance?.onSwivuEditorHide?.();
    };
  }, []);

  return <BryntumWidget ref={ref as unknown as React.RefObject<BryntumWidget>} type={widget.type} cls="w-full h-full flex p-swivu-base pl-8 pt-8" />;
}